<script setup lang="ts">
import { Search, X } from "lucide-vue-next";
import { useQuery } from "@vue/apollo-composable";
import { SearchDocument, TypeOf } from "~/graphql/generated/graphql";
import { useSearchStore, type SearchEntry, type SearchType } from "~/composables/stores/search";
import { keysTyped } from "~/helpers/common-helpers";
import { useSearchTypeMeta } from "~/composables/useSearchTypeMeta";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";

const t = useTranslator();
const chromeStore = useChromeStore();
const searchStore = useSearchStore();

const isOpen = computed(() => chromeStore.searchVisible);
const inputRef = ref<InstanceType<typeof Input>>();
const listRef = ref<HTMLDivElement>();
const { getMeta } = useSearchTypeMeta();

const hasReportsReadPermission = useHasPermissions(PERMISSIONS.REPORTS_READ);
const hasCanvasEditPermission = useHasPermissions(PERMISSIONS.CANVAS_EDIT);
const canUseReportsSearch = computed(() => hasReportsReadPermission.result.value || hasCanvasEditPermission.result.value);

const BASE_SEARCH_TYPE_OPTIONS: { type: SearchType; gqlType: TypeOf }[] = [
  { type: "ASSET_MANAGEMENT_PORTFOLIO", gqlType: TypeOf.AssetManagementPortfolio },
  { type: "ASSET_MANAGEMENT_PROPERTY", gqlType: TypeOf.AssetManagementProperty },
  { type: "ASSET_MANAGEMENT_TENANCY", gqlType: TypeOf.AssetManagementTenancy },
];
const REPORTS_SEARCH_OPTION: { type: SearchType; gqlType: TypeOf } = { type: "CANVAS_LAYOUT", gqlType: TypeOf.CanvasLayout };
const searchTypeOptions = computed<{ type: SearchType; gqlType: TypeOf }[]>(() =>
  canUseReportsSearch.value ? [...BASE_SEARCH_TYPE_OPTIONS, REPORTS_SEARCH_OPTION] : BASE_SEARCH_TYPE_OPTIONS
);
const SEARCH_TYPES_STORAGE_KEY = "search-included-types-v2";
const allTypes = computed<SearchType[]>(() => searchTypeOptions.value.map((item) => item.type));
const selectedTypes = ref<SearchType[]>([]);

const isTypeSelected = (type: SearchType) => selectedTypes.value.includes(type);

const setTypeSelection = (type: SearchType, checked: boolean | "indeterminate") => {
  const include = checked === true;
  if (include) {
    if (!selectedTypes.value.includes(type)) selectedTypes.value = [...selectedTypes.value, type];
    return;
  }
  selectedTypes.value = selectedTypes.value.filter((t) => t !== type);
};

const selectAllTypes = () => {
  selectedTypes.value = [...allTypes.value];
};

const clearAllTypes = () => {
  selectedTypes.value = [];
};

const restoreTypeSelection = () => {
  try {
    const stored = localStorage.getItem(SEARCH_TYPES_STORAGE_KEY);
    if (!stored) {
      selectedTypes.value = [...allTypes.value];
      return;
    }

    const parsed = JSON.parse(stored);
    if (!Array.isArray(parsed)) {
      selectedTypes.value = [...allTypes.value];
      return;
    }

    const valid = parsed.filter((type): type is SearchType => allTypes.value.includes(type as SearchType));
    selectedTypes.value = valid.length > 0 ? valid : [...allTypes.value];
  } catch {
    selectedTypes.value = [...allTypes.value];
  }
};

watch(
  allTypes,
  () => {
    selectedTypes.value = selectedTypes.value.filter((type) => allTypes.value.includes(type));
  },
  { immediate: true }
);

watch(
  selectedTypes,
  (value) => {
    localStorage.setItem(SEARCH_TYPES_STORAGE_KEY, JSON.stringify(value));
  },
  { deep: true }
);

const selectedGraphQlTypes = computed(() => {
  const selectedSet = new Set(selectedTypes.value);
  return searchTypeOptions.value.filter((item) => selectedSet.has(item.type)).map((item) => item.gqlType);
});

// ── Query & debounce ─────────────────────────────────────────────────────────

const debouncing = ref(false);
const searchTermQuery = ref("");
const debounce = useDebounce((val: string) => {
  debouncing.value = false;
  searchTermQuery.value = val;
}, 400);

watch(
  () => searchStore.searchTerm,
  (val) => {
    debouncing.value = true;
    debounce(val);
  }
);

const searchQuery = useQuery(
  SearchDocument,
  () => ({
    input: {
      text: searchTermQuery.value,
      limit: 20,
      offset: 0,
      includedTypes: selectedGraphQlTypes.value,
    },
  }),
  () => ({ enabled: searchTermQuery.value.length > 2 && !searchStore.showingSelected && selectedGraphQlTypes.value.length > 0, errorPolicy: "ignore" })
);

// ── Results ──────────────────────────────────────────────────────────────────

const sortResults = (a: SearchEntry, b: SearchEntry) => {
  const order: SearchType[] = ["ASSET_MANAGEMENT_PORTFOLIO", "ASSET_MANAGEMENT_PROPERTY", "ASSET_MANAGEMENT_TENANCY", "CANVAS_LAYOUT"];
  return order.indexOf(a.type) - order.indexOf(b.type);
};

const searchResults = computed<SearchEntry[]>(() => {
  const raw = searchQuery.result.value?.search?.hits ?? [];
  return raw
    .map((h: any) => ({
      type: h.typeOf as SearchType,
      search: { id: h.entityID, name: h.text },
      fields: h.fields ?? {},
    }))
    .sort(sortResults);
});

const totalResults = computed(() => searchQuery.result.value?.search?.totalResults ?? 0);

const lastSearches = ref<SearchEntry[]>([]);

const fetchStoredSearches = () => {
  try {
    const stored = localStorage.getItem("last-searches");
    if (!stored) return;
    const parsed: SearchEntry[] = JSON.parse(stored);
    if (Array.isArray(parsed)) {
      lastSearches.value = parsed.filter(
        (i) =>
          i !== null &&
          typeof i === "object" &&
          ["ASSET_MANAGEMENT_PORTFOLIO", "ASSET_MANAGEMENT_PROPERTY", "ASSET_MANAGEMENT_TENANCY", "CANVAS_LAYOUT"].includes(i.type) &&
          i.search.name
      );
    }
  } catch {}
};

const isShortQuery = computed(() => searchTermQuery.value.length <= 2);
const loading = computed(() => !isShortQuery.value && (searchQuery.loading.value || debouncing.value));
const selectedTypeSet = computed(() => new Set(selectedTypes.value));
const filteredSearchResults = computed(() => searchResults.value.filter((item) => selectedTypeSet.value.has(item.type)));
const filteredRecentSearches = computed(() => lastSearches.value.filter((item) => selectedTypeSet.value.has(item.type)));

const results = computed<SearchEntry[]>(() =>
  searchStore.showingSelected ? [] : isShortQuery.value ? filteredRecentSearches.value : filteredSearchResults.value
);

watch(results, (v) => (searchStore.results = v), { immediate: true });

// ── Grouped results ──────────────────────────────────────────────────────────

interface ResultGroup {
  type: SearchType;
  items: SearchEntry[];
}

const groupedResults = computed<ResultGroup[]>(() => {
  const order: SearchType[] = ["ASSET_MANAGEMENT_PORTFOLIO", "ASSET_MANAGEMENT_PROPERTY", "ASSET_MANAGEMENT_TENANCY", "CANVAS_LAYOUT"];
  const map = new Map<SearchType, SearchEntry[]>();
  for (const item of results.value) {
    if (!map.has(item.type)) map.set(item.type, []);
    map.get(item.type)!.push(item);
  }
  return order.filter((t) => map.has(t)).map((t) => ({ type: t, items: map.get(t)! }));
});

const groupBaseIndex = (groupIdx: number): number => {
  let idx = 0;
  for (let g = 0; g < groupIdx; g++) idx += groupedResults.value[g]?.items.length ?? 0;
  return idx;
};

// ── Selection ────────────────────────────────────────────────────────────────

const selectedIndex = ref(-1);
watch(results, () => {
  selectedIndex.value = -1;
});

const searchIsEqual = (a: SearchEntry, b: SearchEntry) => keysTyped(a.search).every((k) => a.search[k] === b.search[k]);

const pushToLastSearches = (item: SearchEntry) => {
  let temp = [...lastSearches.value];
  const idx = temp.findIndex((s) => searchIsEqual(s, item));
  if (idx >= 0) temp.splice(idx, 1);
  temp.unshift(item);
  temp = temp.slice(0, 5);
  lastSearches.value = temp;
  localStorage.setItem("last-searches", JSON.stringify(temp));
};

const ampli = useAmplitude();

const select = (item: SearchEntry, index: number) => {
  selectedIndex.value = index;
  searchStore.selection = index;
  searchStore.selectedItem = item;
  pushToLastSearches(item);

  let name: string | undefined;
  let params: Record<string, string> | undefined;

  if (item.type === "ASSET_MANAGEMENT_PORTFOLIO") {
    name = "portfolio-portfolioId";
    params = { portfolioId: item.search.id };
  } else if (item.type === "ASSET_MANAGEMENT_PROPERTY") {
    name = "portfolio-property-propertyId";
    params = { propertyId: item.search.id };
  } else if (item.type === "ASSET_MANAGEMENT_TENANCY") {
    name = "portfolio-tenancy-tenancyId-overview";
    params = { tenancyId: item.search.id };
  } else if (item.type === "CANVAS_LAYOUT") {
    name = "reports-layoutId";
    params = { layoutId: item.search.id };
  }

  ampli.searchSelect({
    searchTerm: searchStore.searchTerm,
    name: item.search.name,
    id: item.search.id,
    type: item.type,
  });

  searchStore.searchTerm = item.search.name;
  if (!name || !params) return;
  navigateTo({ name, params });
  close();
};

const removeFromRecents = (item: SearchEntry) => {
  const idx = lastSearches.value.findIndex((s) => searchIsEqual(s, item));
  if (idx < 0) return;
  const next = [...lastSearches.value];
  next.splice(idx, 1);
  lastSearches.value = next;
  localStorage.setItem("last-searches", JSON.stringify(next));
};

// ── Lifecycle / keyboard ─────────────────────────────────────────────────────

const close = () => chromeStore.closeSearch();

const focusInput = (e: Event) => {
  e.preventDefault();
  nextTick(() => inputRef.value?.$el?.focus());
};

watch(isOpen, (open) => {
  if (open) {
    selectedIndex.value = -1;
    restoreTypeSelection();
    fetchStoredSearches();
  } else {
    searchStore.searchTerm = "";
    searchTermQuery.value = "";
  }
});

const handleKeydown = (e: KeyboardEvent) => {
  if (!isOpen.value) return;
  if (e.key === "ArrowDown") {
    e.preventDefault();
    selectedIndex.value = Math.min(selectedIndex.value + 1, results.value.length - 1);
  }
  if (e.key === "ArrowUp") {
    e.preventDefault();
    selectedIndex.value = Math.max(selectedIndex.value - 1, -1);
  }
  if (e.key === "Enter") {
    e.preventDefault();
    const item = selectedIndex.value >= 0 ? results.value[selectedIndex.value] : results.value[0];
    if (item) select(item, selectedIndex.value >= 0 ? selectedIndex.value : 0);
  }
};

watch(selectedIndex, (idx) => {
  nextTick(() => listRef.value?.querySelector(`[data-idx="${idx}"]`)?.scrollIntoView({ block: "nearest" }));
});

onMounted(() => document.addEventListener("keydown", handleKeydown));
onUnmounted(() => document.removeEventListener("keydown", handleKeydown));

// ── Footer result count ──────────────────────────────────────────────────────

const footerResultCount = computed(() => (!isShortQuery.value && results.value.length > 0 ? totalResults.value || results.value.length : undefined));
</script>

<template>
  <Dialog
    :open="isOpen"
    @update:open="
      (v) => {
        if (!v) close();
      }
    "
  >
    <DialogContent
      class="p-0 max-w-150 gap-0 shadow-none rounded-none sm:rounded-none flex flex-col overflow-hidden [&>button.absolute]:hidden"
      :style="{ maxHeight: 'min(68vh, 640px)' }"
      @open-auto-focus="focusInput"
    >
      <DialogTitle class="sr-only">{{ t("CHROME_SEARCH_INPUT_PLACEHOLDER") }}</DialogTitle>

      <!-- Input row -->
      <div class="flex items-center gap-3 px-4 py-3.5 border-b border-border shrink-0">
        <Search class="w-4.5 h-4.5 shrink-0 text-muted-foreground" :strokeWidth="2" />
        <Input
          ref="inputRef"
          v-model="searchStore.searchTerm"
          :placeholder="t('CHROME_SEARCH_INPUT_PLACEHOLDER')"
          class="flex-1 border-0 bg-transparent h-auto p-0 text-[0.875rem] rounded-none focus-visible:ring-0 focus-visible:ring-offset-0"
        />
        <Button v-if="searchStore.searchTerm" variant="ghost" size="icon-sm" @click="searchStore.searchTerm = ''">
          <X class="w-4 h-4" />
        </Button>
        <kbd v-else class="hidden sm:flex items-center h-5 px-1.5 border border-border text-[0.625rem] text-muted-foreground font-mono bg-muted"> Esc </kbd>
      </div>

      <div class="flex items-center justify-between gap-2 px-4 py-2 border-b border-border/60">
        <div class="flex flex-wrap items-center gap-3">
          <label
            v-for="option in searchTypeOptions"
            :key="option.type"
            class="inline-flex items-center gap-1.5 text-[0.75rem] text-muted-foreground cursor-pointer"
          >
            <Checkbox :model-value="isTypeSelected(option.type)" @update:model-value="(v: boolean | 'indeterminate') => setTypeSelection(option.type, v)" />
            <span>{{ getMeta(option.type).label }}</span>
          </label>
        </div>
        <div class="flex items-center gap-1.5 shrink-0">
          <Button variant="ghost" size="sm" class="h-6 px-2 text-[0.6875rem]" @click="selectAllTypes"> All </Button>
          <Button variant="ghost" size="sm" class="h-6 px-2 text-[0.6875rem]" @click="clearAllTypes"> None </Button>
        </div>
      </div>

      <!-- Results list -->
      <div ref="listRef" class="overflow-y-auto flex-1 min-h-0 py-1.5">
        <!-- Loading -->
        <div v-if="loading" class="px-4 py-3 text-[0.75rem] text-muted-foreground">
          {{ t("CHROME_SEARCH_LOADING") }}
        </div>

        <!-- No results -->
        <SearchEmptyState v-else-if="!isShortQuery && !loading && results.length === 0" :query="searchStore.searchTerm" />

        <!-- Recent searches -->
        <SearchRecentList
          v-else-if="isShortQuery"
          :items="results"
          :selectedIndex="selectedIndex"
          @select="select"
          @hover="(i: number) => selectedIndex = i"
          @remove="removeFromRecents"
        />

        <!-- Grouped search results -->
        <template v-else>
          <SearchResultGroup
            v-for="(group, gi) in groupedResults"
            :key="group.type"
            :type="group.type"
            :items="group.items"
            :selectedIndex="selectedIndex"
            :baseIndex="groupBaseIndex(gi)"
            :highlightQuery="searchTermQuery"
            @select="select"
            @hover="(i: number) => selectedIndex = i"
          />
        </template>
      </div>

      <!-- Footer -->
      <SearchFooter :resultCount="footerResultCount" />
    </DialogContent>
  </Dialog>
</template>
